import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "./ProductSection.module.css";
import { storeRequester } from "App";
import ProductRow from "../ProductRow";
import P from "components/P";
import { eventHandlers } from "./eventHandlers";
import { useGroupedItems } from "shared/hooks/useGroupedItems";
import { toast } from "react-toastify";

const ProductSection = ({
  isMobile,
  cartItems = [],
  setCartItems,
  fetchMedusaCart,
  eventResult,
  eventItemTotalQuantity = [],
  onValidateOrder,
}) => {
  const { t } = useTranslation();
  const groupedItems = useGroupedItems(cartItems, eventItemTotalQuantity);

  // 남은 free item 체크
  useEffect(() => {
    const hasRemainingFreeItems = groupedItems.some(({ eventInfo }) => {
      if (!eventInfo) return false;

      // cross event인 경우
      if (eventInfo.cross) {
        return eventInfo.remainingFreeItems > 0;
      }

      // non-cross event인 경우
      return Object.values(eventInfo.variantCalculations || {}).some(
        (calc) => calc.remainingFreeItems > 0
      );
    });

    onValidateOrder(!hasRemainingFreeItems);
  }, [groupedItems, onValidateOrder]);

  // Helper functions
  const findRelevantEvent = (item, events) => {
    return events?.find((event) =>
      event.products.some((p) => p.id === item.variant?.product?.id)
    );
  };

  const getQuantityUpdatePromises = async (
    item,
    newQuantity,
    cartItems,
    eventInfo
  ) => {
    if (item.free) {
      return [
        await eventHandlers.handleFreeItemQuantity(
          item,
          newQuantity,
          cartItems,
          eventInfo,
          storeRequester
        ),
      ];
    }
    return eventHandlers.handleRegularItemQuantity(
      item,
      newQuantity,
      cartItems,
      eventInfo,
      storeRequester
    );
  };

  const deleteItemAndAdjustFreeItems = async (item, cartItems, eventInfo) => {
    const groupFreeItems = cartItems.filter(
      (i) => i.free && i.title === item.title
    );

    await Promise.all([
      storeRequester.removeItem(item.cart_id, item.id, (res) => {
      }),
      ...groupFreeItems.map((freeItem) =>
        storeRequester.removeItem(freeItem.cart_id, freeItem.id)
      ),
    ]);

    if (eventInfo) {
      const remainingItems = cartItems.filter(
        (i) => i.id !== item.id && !(i.free && i.title === item.title)
      );

      const adjustmentPromises = await eventHandlers.adjustFreeItemsAfterDelete(
        item,
        remainingItems,
        eventInfo,
        storeRequester
      );

      if (adjustmentPromises.length > 0) {
        await Promise.all(adjustmentPromises);
      }
    }
  };

  // Event handlers
  const handleQuantityChange = async (item, newQuantity) => {
    const originalQuantity = item.quantity;

    try {
      if (item.variant.inventory_quantity <= 0) {
        await handleDelete(item);
        toast.error(
          `재고량 부족으로 인해 ${item.title}${
            item.variant?.title ? item.variant.title : ""
          }이 제거되었습니다`,
          { autoClose: 1000, position: "bottom-center" }
        );
        return;
      }

      if (newQuantity <= 0 || newQuantity > item.variant.inventory_quantity) {
        // await handleDelete(item);
        return;
      }

      const relevantEvent = findRelevantEvent(item, eventItemTotalQuantity);
      await getQuantityUpdatePromises(
        item,
        newQuantity,
        cartItems,
        relevantEvent
      );

      // 순서를 유지하면서 cart를 새로고침
      const updatedCart = await fetchMedusaCart();

      // cart 업데이트 후에도 원래 아이템의 순서를 유지
      const updatedItems = updatedCart.items.map((newItem) => {
        const originalItem = cartItems.find(
          (origItem) => origItem.id === newItem.id
        );
        if (originalItem) {
          return {
            ...newItem,
            created_at: originalItem.created_at,
          };
        }
        return newItem;
      });

      setCartItems(updatedItems);
    } catch (error) {
      
      toast.error("수량 변경에 실패했습니다. 다시 시도해주세요.");

      // 실패 시 원래 수량으로 되돌리기
      try {
        const relevantEvent = findRelevantEvent(item, eventItemTotalQuantity);
        await getQuantityUpdatePromises(
          item,
          originalQuantity,
          cartItems,
          relevantEvent
        );
        await fetchMedusaCart();
      } catch (rollbackError) {
        
        toast.error("오류가 발생했습니다. 페이지를 새로고침해주세요.");
      }
    }
  };

  const handleDelete = async (item) => {
    try {
      const relevantEvent = findRelevantEvent(item, eventItemTotalQuantity);

      if (item.free) {
        await storeRequester.removeItem(item.cart_id, item.id, (res) => {
        });
      } else {
        await deleteItemAndAdjustFreeItems(item, cartItems, relevantEvent, (res) => {
        });
      }
      await fetchMedusaCart();
    } catch (error) {
      
    }
  };

  const handleAddFreeItem = async (
    event,
    variant,
    parentItem,
    quantity = 1
  ) => {
    try {
      await storeRequester.addItem(parentItem.cart_id, {
        variant_id: variant.id,
        quantity: quantity,
        free: true,
        metadata: {
          event_id: event.eventId,
          parent_variant_id: parentItem.variant_id,
        },
      });
      await fetchMedusaCart();
    } catch (error) {
      
      toast.error("사은품 추가에 실패했습니다.");
    }
  };

  return (
    <div className={style.container}>
      <P marginBottom={15} weight="bold" size={24} className={style.title}>
        {t("주문상품")}
      </P>
      <div className={style.boldDivider} />
      <div className={style.productList}>
        {groupedItems.map(({ title, items, eventInfo }) => (
          <ProductRow
            key={title}
            items={items}
            eventInfo={eventInfo}
            onQuantityChange={handleQuantityChange}
            onDelete={handleDelete}
            onAddFreeItem={handleAddFreeItem}
            isMobile={isMobile}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductSection;
